import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {clientI18n, clientIntl} from '../ClientI18n'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper'
import {ViewPopup} from '../ui_popup/ViewPopup'
import {createDivIn, createSpanIn, createSelectIn, createOptionIn, setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils'
import {ContinentCode} from '@colonist/leaderboard-types'
import {TranslationObject} from '@colonist/i18n'
import {TabCommunicationEvent} from '../tab_communication/TabCommunication'
import {tabCommunication} from '../EntryShared'
import {uniq} from 'lodash-es'
import {isValueNonEmptyString} from '@colonist/validation'

interface GeographicalLocation {
    name: string
    code: string
}

export class UIProfileSettingsRegionChangeController {
    private readonly changeRegionButton: HTMLDivElement

    constructor() {
        this.changeRegionButton = document.getElementById('page-profile-settings-change-region') as HTMLDivElement
        setElementOnClick(this.changeRegionButton, () => UIProfileSettingsRegionChangeController.showChangeRegionPopup())
    }

    static showChangeRegionPopup() {
        new UIProfileSettingsRegionChangePopup()
    }
}

class UIProfileSettingsRegionChangePopup {
    private continentsSelectElement: HTMLSelectElement | undefined
    private countriesSelectElement: HTMLSelectElement | undefined
    private statesSelectElement: HTMLSelectElement | undefined
    private readonly popup: ViewPopup
    private readonly regions: AjaxDataTypes.Region[] = []

    constructor() {
        this.popup = new ViewPopup({key: 'strings:popups.changeRegion.title'},
            {key: 'strings:popups.changeRegion.body'},
            () => this.handleChangeRegion(),
            () => undefined,
            true)

        this.popup.body.classList.add('page-profile-change-region-popup-container')

        createDivIn(this.popup.body, 'loading-spinner')

        this.popup.disableCheckButton()

        this.popup.show()

        void this.loadData()
    }

    private async loadData() {
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiGetRegions())
        if(data?.json == undefined) return
        const {regions} = data?.json as AjaxDataTypes.Regions

        this.regions.push(...regions)

        this.createRegionSelection()
        this.popup.body.getElementsByClassName('loading-spinner')[0]?.remove()
    }

    private createRegionSelection() {
        const regionsSelectContainer = createDivIn(this.popup.body, 'page-profile-change-region-selects-container')
        this.continentsSelectElement = UIProfileSettingsRegionChangePopup.createSelector(regionsSelectContainer, {key: 'strings:colonistCommon.continent'}, (continentCode) => {
            const continentSelected = (continentCode != undefined) && (continentCode.trim() != '')
            if(continentSelected) {
                this.popup.enableCheckButton()
            } else {
                this.popup.disableCheckButton()
            }

            this.setCountries(continentCode)
        })

        this.countriesSelectElement = UIProfileSettingsRegionChangePopup.createSelector(regionsSelectContainer, {key: 'strings:colonistCommon.country'}, (countryCode) => this.setState(countryCode))

        this.statesSelectElement = UIProfileSettingsRegionChangePopup.createSelector(regionsSelectContainer, {key: 'strings:colonistCommon.region'}, () => undefined)

        this.setContinents()
        UIProfileSettingsRegionChangePopup.setDefaultOption(this.countriesSelectElement)
        UIProfileSettingsRegionChangePopup.setDefaultOption(this.statesSelectElement)
    }

    private async handleChangeRegion() {
        const continentCode = this.continentsSelectElement?.value
        const countryCode = this.countriesSelectElement?.value
        const stateCode = this.statesSelectElement?.value

        const data: AjaxDataTypes.ChangeRegionData = {
            continentCode: !continentCode ? undefined : continentCode as ContinentCode,
            countryCode: !countryCode ? undefined : countryCode,
            stateCode: !stateCode ? undefined : stateCode,
        }
        const response = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiChangeRegion(), undefined, data)
        if(response == undefined) return

        if(!response.isSuccess) return

        const popup = new ViewPopup({key: 'strings:popups.changeRegion.title'}, {key: 'strings:popups.changeRegion.success'}, () => undefined, () => undefined, true)
        popup.removeAction = () => {
            window.location.reload()
            tabCommunication.sendEvent(TabCommunicationEvent.ReloadWebsite)
        }
        popup.removeXButton()
        popup.show()
    }

    private static createSelector(container: HTMLDivElement, labelText: TranslationObject, onSelect: (code: string) => void) {
        const selectContainer = createDivIn(container, 'page-profile-change-region-select-container')
        createSpanIn(selectContainer, '', labelText)
        const selectElement = createSelectIn(selectContainer, '', '', onSelect)
        return selectElement
    }

    private setContinents() {
        if(this.continentsSelectElement == undefined) return

        UIProfileSettingsRegionChangePopup.setEmptyOption(this.continentsSelectElement)

        const continentCodes = uniq(this.regions.map((region) => region.continentCode))

        const continents: GeographicalLocation[] = []
        for(const continentCode of continentCodes) {
            const continentName = clientI18n.t(`strings:leaderboardPage.tabs.continents.${continentCode}`)
            continents.push({name: continentName, code: continentCode})
        }
        continents.sort((a, b) => a.name.localeCompare(b.name))

        for(const continent of continents) {
            createOptionIn(this.continentsSelectElement, continent.code, {key: 'strings:colonistCommon.utils.#buffer', options: {value: continent.name, interpolation: {escapeValue: false}}})
        }
    }

    private setCountries(continentCode: string) {
        if(this.countriesSelectElement == undefined) return
        if(this.statesSelectElement == undefined) return

        setElementInnerHTML(this.countriesSelectElement, {key: 'strings:colonistCommon.utils.emptyString'})
        setElementInnerHTML(this.statesSelectElement, {key: 'strings:colonistCommon.utils.emptyString'})

        UIProfileSettingsRegionChangePopup.setDefaultOption(this.countriesSelectElement)
        UIProfileSettingsRegionChangePopup.setDefaultOption(this.statesSelectElement)

        if(!isValueNonEmptyString(continentCode)) return

        const countryCodes = uniq(this.regions.filter((region) => region.continentCode == continentCode).map((region) => region.countryCode))

        const countries: GeographicalLocation[] = []
        for(const countryCode of countryCodes) {
            const countryName = clientIntl.getCountryName(countryCode)
            if(countryName == undefined) continue
            countries.push({name: countryName, code: countryCode})
        }
        countries.sort((a, b) => a.name.localeCompare(b.name))

        for(const country of countries) {
            createOptionIn(this.countriesSelectElement, country.code, {key: 'strings:colonistCommon.utils.#buffer', options: {value: country.name, interpolation: {escapeValue: false}}})
        }
    }

    private setState(countryCode: string) {
        if(this.statesSelectElement == undefined) return

        setElementInnerHTML(this.statesSelectElement, {key: 'strings:colonistCommon.utils.emptyString'})
        UIProfileSettingsRegionChangePopup.setDefaultOption(this.statesSelectElement)

        if(!isValueNonEmptyString(countryCode)) return

        const regions = this.regions.filter((region) => region.countryCode == countryCode)

        const states: GeographicalLocation[] = []
        for(const region of regions) {
            states.push({name: region.regionName, code: region.regionCode})
        }
        states.sort((a, b) => a.name.localeCompare(b.name))

        for(const state of states) {
            createOptionIn(this.statesSelectElement, state.code, {key: 'strings:colonistCommon.utils.#buffer', options: {value: state.name, interpolation: {escapeValue: false}}})
        }
    }

    private static setDefaultOption(selectElement: HTMLSelectElement) {
        createOptionIn(selectElement, '', {key: 'strings:popups.changeRegion.doNotDisplay'})
    }

    private static setEmptyOption(selectElement: HTMLSelectElement) {
        createOptionIn(selectElement, '', {key: 'strings:colonistCommon.utils.#buffer', options: {value: ''}})
    }
}
